import React, { useEffect, useState, useMemo } from 'react';
import API from '../api';
import '../styles/global.css';
import { toast } from 'react-toastify';

export default function ActivityLogPage() {
  // ... existing state ...
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModule, setFilterModule] = useState('ALL');
  const [filterAction, setFilterAction] = useState('ALL');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // --- NEW STATE FOR MODAL ---
  const [selectedLog, setSelectedLog] = useState(null);

  const ITEMS_PER_PAGE = 15;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/activity-logs');
      setLogs(data);
    } catch (error) { toast.error('Failed to load activity logs'); } 
    finally { setLoading(false); }
  };

  // ... (Keep existing filtering/stats logic here) ...
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const matchesModule = filterModule === 'ALL' || log.module === filterModule;
      const matchesAction = filterAction === 'ALL' || log.action === filterAction;
      const matchesSearch = (log.description || '').toLowerCase().includes(search.toLowerCase()) || 
                            (log.performerName || '').toLowerCase().includes(search.toLowerCase());
      
      let matchesDate = true;
      if (dateRange !== 'ALL') {
        const logDate = new Date(log.createdAt);
        const now = new Date();
        const diffDays = Math.floor((now - logDate) / (1000 * 60 * 60 * 24));
        if (dateRange === 'TODAY') matchesDate = diffDays === 0;
        else if (dateRange === 'WEEK') matchesDate = diffDays <= 7;
        else if (dateRange === 'MONTH') matchesDate = diffDays <= 30;
      }
      return matchesModule && matchesAction && matchesSearch && matchesDate;
    });
  }, [logs, filterModule, filterAction, search, dateRange]);

  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const stats = useMemo(() => ({
    total: logs.length,
    creates: logs.filter(l => l.action === 'CREATE').length,
    updates: logs.filter(l => l.action === 'UPDATE').length,
    deletes: logs.filter(l => l.action === 'DELETE').length,
    today: logs.filter(l => {
      const d = new Date(l.createdAt);
      const now = new Date();
      return d.toDateString() === now.toDateString();
    }).length
  }), [logs]);

  // --- STYLES ---
  const styles = {
    // ... (Keep all your existing styles from the previous version) ...
    container: { minHeight: '100vh', background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', fontFamily: "'Inter', sans-serif", padding: isMobile ? '16px' : '32px 40px' },
    header: { marginBottom: 32 },
    headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20, marginBottom: 28 },
    headerLeft: { display: 'flex', gap: 16, alignItems: 'center' },
    iconBox: { width: 56, height: 56, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)' },
    pageTitle: { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0 },
    pageSubtitle: { fontSize: 15, color: '#64748b', margin: '6px 0 0' },
    refreshBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#475569', cursor: 'pointer' },
    
    // Stats & Filters
    statsGrid: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 16, marginBottom: 28 },
    statCard: { background: 'white', borderRadius: 14, padding: '20px 24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    statValue: { fontSize: 26, fontWeight: 800, color: '#0f172a' },
    statLabel: { fontSize: 13, color: '#64748b', fontWeight: 500 },
    
    filterSection: { background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 24px', marginBottom: 24 },
    filterGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr', gap: 12 },
    searchInput: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #e2e8f0', fontSize: 14, background: '#f8fafc', boxSizing:'border-box' },
    select: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #e2e8f0', fontSize: 14, background: '#f8fafc', cursor: 'pointer' },

    // Table
    tableCard: { background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' },
    tableHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', background: '#fafbfc' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { background: '#f8fafc' },
    th: { padding: '14px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' },
    
    // Updated TR for clicking
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'all 0.15s ease', cursor: 'pointer' }, 
    trHover: { background: '#f1f5f9' },
    
    td: { padding: '18px 24px', fontSize: 14, color: '#334155' },
    badge: { padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 },
    
    // --- NEW MODAL STYLES ---
    modalOverlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20, animation: 'fadeIn 0.2s ease-out'
    },
    modalContent: {
      background: 'white', width: '100%', maxWidth: 600,
      borderRadius: 20, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden', animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
    },
    modalHeader: {
      padding: '24px 32px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    },
    modalBody: { padding: '32px' },
    modalRow: { display: 'flex', marginBottom: 24, gap: 24 },
    modalCol: { flex: 1 },
    modalLabel: { fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
    modalValue: { fontSize: 15, fontWeight: 500, color: '#1e293b', lineHeight: 1.5 },
    modalValueBig: { fontSize: 18, fontWeight: 600, color: '#0f172a' },
    modalCloseBtn: {
      background: 'transparent', border: 'none', fontSize: 24, color: '#94a3b8',
      cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center'
    },
    divider: { height: 1, background: '#e2e8f0', margin: '24px 0' },
    rawJson: {
      background: '#1e293b', color: '#e2e8f0', padding: 16, borderRadius: 12,
      fontSize: 12, fontFamily: 'monospace', overflowX: 'auto', margin: 0
    }
  };

  // Helper styles for badges (Same as before)
  const getActionBadge = (action) => {
    const s = {
      CREATE: { bg: '#ecfdf5', c: '#047857', b: '#a7f3d0' },
      UPDATE: { bg: '#eff6ff', c: '#1d4ed8', b: '#93c5fd' },
      DELETE: { bg: '#fef2f2', c: '#be123c', b: '#fca5a5' },
      LOGIN:  { bg: '#faf5ff', c: '#7c3aed', b: '#c4b5fd' }
    }[action] || { bg: '#f1f5f9', c: '#64748b', b: '#cbd5e1' };
    
    return { background: s.bg, color: s.c, border: `1px solid ${s.b}` };
  };

  // --- RENDER MODAL ---
  const renderDetailsModal = () => {
    if (!selectedLog) return null;

    const log = selectedLog;
    const badgeStyle = getActionBadge(log.action);
    const date = new Date(log.createdAt);

    return (
      <div style={styles.modalOverlay} onClick={() => setSelectedLog(null)}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          
          {/* Header */}
          <div style={styles.modalHeader}>
            <div style={{display:'flex', alignItems:'center', gap: 12}}>
              <span style={{...styles.badge, ...badgeStyle, fontSize: 13, padding: '6px 12px'}}>
                {log.action}
              </span>
              <span style={{color: '#94a3b8'}}>|</span>
              <span style={{fontWeight: 600, color: '#475569'}}>{log.module}</span>
            </div>
            <button style={styles.modalCloseBtn} onClick={() => setSelectedLog(null)}>×</button>
          </div>

          {/* Body */}
          <div style={styles.modalBody}>
            
            {/* Top Section: Who and When */}
            <div style={styles.modalRow}>
              <div style={styles.modalCol}>
                <div style={styles.modalLabel}>Performed By</div>
                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                  <div style={{width: 40, height: 40, borderRadius: '50%', background: '#eff6ff', color: '#3b82f6', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700}}>
                    {log.performerName?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontWeight: 600, color: '#1e293b'}}>{log.performerName}</div>
                    <div style={{fontSize: 12, color: '#64748b'}}>{log.role}</div>
                  </div>
                </div>
              </div>
              <div style={{...styles.modalCol, textAlign: 'right'}}>
                <div style={styles.modalLabel}>Timestamp</div>
                <div style={styles.modalValue}>{date.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                <div style={{fontSize: 13, color: '#64748b'}}>{date.toLocaleTimeString('en-IN')}</div>
              </div>
            </div>

            <div style={styles.divider} />

            {/* Middle Section: What */}
            <div style={{marginBottom: 24}}>
              <div style={styles.modalLabel}>Description</div>
              <div style={styles.modalValueBig}>{log.description}</div>
            </div>

            {/* If you have specific ID or target info */}
            <div style={styles.modalRow}>
              <div style={styles.modalCol}>
                <div style={styles.modalLabel}>Module Target</div>
                <div style={styles.modalValue}>{log.module}</div>
              </div>
              <div style={styles.modalCol}>
                <div style={styles.modalLabel}>Log ID</div>
                <div style={{fontFamily: 'monospace', color: '#64748b', fontSize: 13}}>{log._id}</div>
              </div>
            </div>

            {/* Optional: Raw Data View (Useful for devs/admins) */}
            <div style={{marginTop: 30}}>
              <div style={styles.modalLabel}>System Data</div>
              <pre style={styles.rawJson}>
{JSON.stringify({
  action: log.action,
  module: log.module,
  performerId: log.performerId || 'N/A',
  ip: log.ipAddress || '127.0.0.1 (simulated)', // If backend has this
  userAgent: 'Mozilla/5.0...' // If backend has this
}, null, 2)}
              </pre>
            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ... Header and Stats sections (Same as previous code) ... */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerLeft}>
            <div style={styles.iconBox}>📋</div>
            <div>
              <h1 style={styles.pageTitle}>Activity Log</h1>
              <p style={styles.pageSubtitle}>Click on any row to view full details</p>
            </div>
          </div>
          <button style={styles.refreshBtn} onClick={fetchLogs}>Refresh</button>
        </div>
        
        {/* Shortened Stats for brevity in this snippet, use previous full stats grid */}
        <div style={styles.statsGrid}>
           <div style={styles.statCard}><div style={styles.statValue}>{stats.total}</div><div style={styles.statLabel}>Total Events</div></div>
           <div style={styles.statCard}><div style={{...styles.statValue, color:'#10b981'}}>{stats.creates}</div><div style={styles.statLabel}>Created</div></div>
           <div style={styles.statCard}><div style={{...styles.statValue, color:'#6366f1'}}>{stats.updates}</div><div style={styles.statLabel}>Updated</div></div>
           <div style={styles.statCard}><div style={{...styles.statValue, color:'#ef4444'}}>{stats.deletes}</div><div style={styles.statLabel}>Deleted</div></div>
           <div style={styles.statCard}><div style={{...styles.statValue, color:'#8b5cf6'}}>{stats.today}</div><div style={styles.statLabel}>Today</div></div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterSection}>
        <div style={styles.filterGrid}>
           <input style={styles.searchInput} placeholder="Search logs..." value={search} onChange={(e)=>setSearch(e.target.value)} />
           <select style={styles.select} value={filterModule} onChange={(e)=>setFilterModule(e.target.value)}><option value="ALL">All Modules</option><option value="Medicine">Medicine</option><option value="Billing">Billing</option></select>
           <select style={styles.select} value={filterAction} onChange={(e)=>setFilterAction(e.target.value)}><option value="ALL">All Actions</option><option value="CREATE">Create</option><option value="UPDATE">Update</option><option value="DELETE">Delete</option></select>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableCard}>
        <div style={{overflowX: 'auto'}}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>Staff Member</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Description</th>
                {!isMobile && <th style={styles.th}>Module</th>}
                <th style={{...styles.th, textAlign:'right'}}>Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="5" style={{padding:40, textAlign:'center'}}>Loading...</td></tr> : 
               paginatedLogs.map(log => (
                <tr 
                  key={log._id} 
                  style={{...styles.tr, ...(hoveredRow === log._id ? styles.trHover : {})}}
                  onMouseEnter={() => setHoveredRow(log._id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => setSelectedLog(log)} // <--- CLICK HANDLER
                >
                  <td style={styles.td}>
                    <div style={{display:'flex', gap:12, alignItems:'center'}}>
                      <div style={{width:32, height:32, background:'#eff6ff', color:'#3b82f6', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700}}>
                        {log.performerName?.charAt(0)}
                      </div>
                      <span style={{fontWeight:500, color:'#1e293b'}}>{log.performerName}</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{...styles.badge, ...getActionBadge(log.action)}}>{log.action}</span>
                  </td>
                  <td style={{...styles.td, maxWidth:300, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {log.description}
                  </td>
                  {!isMobile && <td style={styles.td}>{log.module}</td>}
                  <td style={{...styles.td, textAlign:'right', color:'#64748b', fontSize:13}}>
                    {new Date(log.createdAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Render the Modal if a log is selected */}
      {renderDetailsModal()}
      
    </div>
  );
}